import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import {
  TextField,
  Paper,
  Button,
  Divider,
  MenuItem,
  Box,
} from '@material-ui/core';
import { ArrowLeft } from '@material-ui/icons';
import { useDispatch } from 'react-redux';
import useStyles from './styles';
import CustomButton from '../CustomButton';
import { Webhook, WebhookFormType } from '../../redux/types/webhooks';
import { ActionPayload } from '../../types/main';
import {
  createWebhookRequest,
  webhookDetailsRequest,
  updateWebhookRequest,
} from '../../redux/actions/webhooks';

import webhookSchema from './WebhookValidation';
import { ValidationErrors, beautifyErrors } from '../../utils/helpers';
import { Query } from '../../utils/query';
import { Builder } from '../../redux/types/builders';
import { testWebhook } from '../../services/webhooks';
import {
  createSuccessSnackBar,
  createErrorSnackBar,
} from '../../redux/actions/snackbars';
import { WebhookTestModal } from './WebhookTestModal';
import { usePermissions } from '../../hooks/usePermissions';

type Props = {
  webhooks: Webhook[];
  selectedWebhook: Webhook | null;
  createWebhook: (webhook: ActionPayload<typeof createWebhookRequest>) => void;
  updateWebhook: (webhook: ActionPayload<typeof updateWebhookRequest>) => void;
  loadWebhooks: (query?: Query) => void;
  loadWebhookById: (
    webhook: ActionPayload<typeof webhookDetailsRequest>
  ) => void;
  isEnterprise: boolean;
  enterpriseId: number | null;
  builderId: number;
  enterpriseBuilders: Builder[];
  loadingEnterpriseBuilders: boolean;
  loadEnterpriseBuilders: (enterpriseId: number) => void;
  loadCommunityProperties: (communityId: number) => void;
};

type Select = {
  label: string;
  value: string;
};

const contentTypes: Select[] = [
  { label: 'application/json', value: 'json' },
  { label: 'text/html', value: 'html' },
  { label: 'text/plain', value: 'plain' },
];

const eventTypes: Select[] = [
  { label: 'Tours Enable', value: 'tours.enable' },
  { label: 'Tours Disable', value: 'tours.disable' },
  { label: 'On Demand Tours', value: 'on.demand.tours' },
  { label: 'Scheduled Tours', value: 'scheduled.tours' },
  { label: 'Post Survey Tours', value: 'post.survey.tours' },
];

type TestWebhook = {
  communityId: number;
  propertyId: number;
};

const WebhookForm: React.FC<Props> = (props) => {
  const {
    webhooks,
    selectedWebhook,
    createWebhook,
    updateWebhook,
    loadWebhooks,
    loadWebhookById,
    isEnterprise,
    enterpriseId,
    builderId,
    enterpriseBuilders,
    loadingEnterpriseBuilders,
    loadEnterpriseBuilders,
    loadCommunityProperties,
  } = props;
  const classes = useStyles();
  const history = useHistory();
  const { webhookId } = useParams<{ webhookId?: string }>();

  const [webhookForm, setWebhookForm] = useState<WebhookFormType>({
    payload_url: '',
    content_type: '',
    secret: '',
    event_type: '',
    header_name: '',
  });
  const [validationErrors, setValidationErrors] = useState<
  ValidationErrors<Webhook>
  >({});
  const [selectedBuilder, setSelectedBuilder] = useState<number | null>(
    builderId,
  );
  const [builders, setBuilders] = useState<Builder[]>(enterpriseBuilders);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [testWebhookPayload, setTestWebhookPayload] = useState<TestWebhook | null>(null);

  const { canEdit } = usePermissions();

  const dispatch = useDispatch();

  useEffect(() => {
    if (builderId > 0) {
      const enterpriseBuilder = enterpriseBuilders.find(
        (eB) => eB.builder_id === builderId,
      );
      if (enterpriseBuilder) {
        setBuilders([enterpriseBuilder]);
        setSelectedBuilder(enterpriseBuilder.builder_id);
        setWebhookForm((prevState) => ({
          ...prevState,
          builder_id: enterpriseBuilder.builder_id,
        }));
      }
    } else {
      setBuilders(enterpriseBuilders);
      setSelectedBuilder(null);
    }
  }, [builderId, selectedBuilder, enterpriseBuilders]);

  useEffect(() => {
    if (enterpriseId) {
      loadEnterpriseBuilders(enterpriseId);
    }
  }, [loadEnterpriseBuilders, enterpriseId]);

  useEffect(() => {
    loadWebhooks();
  }, [loadWebhooks, builderId]);

  useEffect(() => {
    if (!webhookId) {
      setWebhookForm({
        payload_url: '',
        content_type: contentTypes[0].label,
        secret: '',
        event_type: '',
        header_name: '',
      });
      return;
    }
    loadWebhookById(webhookId);
  }, [webhookId, loadWebhookById]);

  useEffect(() => {
    if (!selectedWebhook || !webhookId) {
      return;
    }

    setWebhookForm({
      payload_url: selectedWebhook.payload_url,
      content_type: selectedWebhook.content_type,
      secret: selectedWebhook.secret,
      event_type: selectedWebhook.event_type,
      header_name: selectedWebhook.header_name,
    });
  }, [selectedWebhook, webhookId]);

  const validateForm = () => {
    try {
      const webhookToValidate = { ...webhookForm, builder_id: selectedBuilder };
      webhookSchema.validateSync(webhookToValidate, {
        abortEarly: false,
        context: {
          isEnterprise,
        },
      });
      setValidationErrors({});
      return true;
    } catch (error) {
      setValidationErrors(beautifyErrors<Webhook>(error));
      return false;
    }
  };

  const handleSubmit = () => {
    const isFormValid = validateForm();

    if (!isFormValid) {
      return;
    }

    const payload = {
      ...webhookForm,
      header_name: webhookForm.header_name?.trim() || 'NterNow-X-Api',
    };

    if (webhookId) {
      updateWebhook({
        webhookId,
        webhook: payload,
      });
      return;
    }

    createWebhook(payload);
  };

  const handleChangeWebhookForm = ({
    target: { name, value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setWebhookForm((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleChangeContentType = ({
    target: { name, value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setWebhookForm((prevState) => ({
      ...prevState,
      [name]: contentTypes.find((c) => c.value === value)?.label,
    }));
  };

  const handleChangeEventType = ({
    target: { name, value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setWebhookForm((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSelectBuilder = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedBuilderId = Number(e.target.value);
    const selected = builders.find(
      ({ builder_id }) => builder_id === selectedBuilderId,
    );

    if (selected) {
      setSelectedBuilder(selectedBuilderId);
      setWebhookForm((prevState) => ({
        ...prevState,
        builder_id: selectedBuilderId,
      }));
    }
  };

  const handleTest = () => {
    const isFormValid = validateForm();

    if (!isFormValid) {
      return;
    }

    setOpenModal(true);
  };

  const testNewWebhook = async () => {
    try {
      const payload = {
        ...webhookForm,
        header_name: webhookForm.header_name?.trim() || 'NterNow-X-Api',
        propertyId: testWebhookPayload?.propertyId,
        communityId: testWebhookPayload?.communityId,
      };
      const { data } = await testWebhook(payload);
      dispatch(createSuccessSnackBar(data));
      setOpenModal(false);
    } catch (e) {
      dispatch(createErrorSnackBar('The webhook test failed. Please check the data you entered!'));
      setOpenModal(false);
    }
  };

  const handleChangeCommunity = (value: number) => {
    loadCommunityProperties(value);
    setTestWebhookPayload((prevState) => ({
      propertyId: prevState?.propertyId ?? 0,
      communityId: value,
    }));
  };

  const handleChangeProperty = (value: number) => {
    setTestWebhookPayload((prevState) => ({
      communityId: prevState?.communityId ?? 0,
      propertyId: value,
    }));
  };


  return (
    <>
      <Grid container justify="space-between" style={{ marginBottom: 25 }}>
        <Grid item>
          <Button variant="contained" onClick={() => history.push('/webhooks')}>
            <ArrowLeft />
            {' GoBack'}
          </Button>
        </Grid>
      </Grid>
      <Paper className={classes.paper}>
        <div className={classes.headerText}>
          <span>{webhookId ? 'Edit Webhook' : 'Add New Webhook'}</span>
        </div>
        <div className={classes.inputForm}>
          <Box justifyContent="end" display="flex">
            <CustomButton
              className={classes.testButton}
              variant="orange"
              onClick={handleTest}
              disabled={!canEdit}
            >
              Test Webhook
            </CustomButton>
          </Box>
          {isEnterprise && (
            <Grid item xs={12}>
              <TextField
                select
                required
                fullWidth
                margin="normal"
                id="builder"
                name="builder"
                label="Select Builder"
                value={selectedBuilder}
                onChange={handleSelectBuilder}
                disabled={!enterpriseId && loadingEnterpriseBuilders}
                error={Boolean(validationErrors.builder_id)}
                helperText={validationErrors.builder_id}
              >
                <MenuItem value="" disabled>
                  Select Builder
                </MenuItem>
                {builders?.map(({ builder_id, name }) => (
                  <MenuItem key={builder_id} value={builder_id}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} sm={12}>
            <TextField
              autoComplete="off"
              onChange={handleChangeWebhookForm}
              margin="normal"
              required
              value={webhookForm.payload_url}
              fullWidth
              label="Payload URL"
              name="payload_url"
              error={Boolean(validationErrors.payload_url)}
              helperText={validationErrors.payload_url}
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <TextField
              select
              SelectProps={{
                MenuProps: {
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null,
                },
              }}
              onChange={handleChangeContentType}
              margin="normal"
              fullWidth
              label="Content Type"
              name="content_type"
              value={
                contentTypes.find((c) => c.label === webhookForm.content_type)
                  ?.value || ''
              }
              error={Boolean(validationErrors.content_type)}
              helperText={validationErrors.content_type}
              disabled={!canEdit}
            >
              <MenuItem value="" disabled>
                Select Content Type
              </MenuItem>
              {contentTypes.map((content, index) => (
                <MenuItem
                  key={content.label}
                  value={content.value}
                  selected={index === 0}
                >
                  {content.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={12}>
            <TextField
              select
              SelectProps={{
                MenuProps: {
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null,
                },
              }}
              onChange={handleChangeEventType}
              margin="normal"
              fullWidth
              label="Event Type"
              name="event_type"
              value={
                eventTypes.find((c) => c.value === webhookForm.event_type)
                  ?.value || ''
              }
              error={Boolean(validationErrors.event_type)}
              helperText={validationErrors.event_type}
              disabled={!canEdit}
            >
              <MenuItem value="" disabled>
                Select Event Type
              </MenuItem>
              {eventTypes.map((content, index) => {
                const isEventTypeExists = webhooks.some(
                  (webhook) => webhook.event_type === content.value,
                );

                return (
                  <MenuItem
                    key={content.label}
                    value={content.value}
                    selected={index === 0}
                    disabled={isEventTypeExists}
                  >
                    {content.value}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={12}>
            <TextField
              autoComplete="off"
              onChange={handleChangeWebhookForm}
              margin="normal"
              value={webhookForm.secret}
              fullWidth
              label="Secret"
              name="secret"
              type="text"
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <TextField
              autoComplete="off"
              onChange={handleChangeWebhookForm}
              margin="normal"
              value={webhookForm.header_name}
              fullWidth
              label="Header name"
              name="header_name"
              type="text"
              disabled={!canEdit}
            />
          </Grid>
        </div>
        <Divider />
        <CustomButton
          className={classes.submitButton}
          variant="orange"
          onClick={handleSubmit}
          disabled={!canEdit}
        >
          {webhookId ? 'Update' : 'Create'}
        </CustomButton>
      </Paper>
      <WebhookTestModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        builderId={builderId}
        handleChangeCommunity={handleChangeCommunity}
        handleChangeProperty={handleChangeProperty}
        handleSend={testNewWebhook}
      />
    </>
  );
};

export default WebhookForm;
